import { XIcon } from "@phosphor-icons/react";
import { type ReactNode, useId } from "react";
import { Button, Dialog as D, Heading, Modal, ModalOverlay } from "react-aria-components";
import { cn } from "./utils";

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const descriptionId = useId();
  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={onOpenChange}
      isDismissable
      className="animate-fade-in fixed inset-0 z-50 bg-black/30 backdrop-blur-[1px]"
    >
      <Modal>
        <D
          aria-describedby={description ? descriptionId : undefined}
          className={cn(
            "animate-fade-in fixed top-[12vh] left-1/2 z-50 flex max-h-[80vh] w-[calc(100vw-32px)] max-w-md -translate-x-1/2 flex-col rounded-lg border border-border bg-bg shadow-md outline-none",
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5">
            <div>
              <Heading slot="title" className="text-[15px] font-semibold">
                {title}
              </Heading>
              {description && (
                <p id={descriptionId} className="mt-1 text-[13px] text-fg-muted">
                  {description}
                </p>
              )}
            </div>
            <Button
              slot="close"
              className="rounded-md p-1 text-fg-subtle hover:bg-muted hover:text-fg"
              aria-label="Close"
            >
              <XIcon className="size-4" />
            </Button>
          </div>
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-1">{children}</div>}
          {footer && <div className="flex justify-end gap-2 px-5 pt-4 pb-5">{footer}</div>}
        </D>
      </Modal>
    </ModalOverlay>
  );
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = "max-w-xl",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const descriptionId = useId();
  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={onOpenChange}
      isDismissable
      className="animate-fade-in fixed inset-0 z-50 bg-black/20"
    >
      <Modal>
        <D
          aria-describedby={description ? descriptionId : undefined}
          className={cn(
            "animate-slide-in fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-bg shadow-md outline-none",
            width,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div className="min-w-0">
              <Heading slot="title" className="truncate text-[15px] font-semibold">
                {title}
              </Heading>
              {description && (
                <p id={descriptionId} className="mt-0.5 text-[13px] text-fg-muted">
                  {description}
                </p>
              )}
            </div>
            <Button
              slot="close"
              className="rounded-md p-1 text-fg-subtle hover:bg-muted hover:text-fg"
              aria-label="Close"
            >
              <XIcon className="size-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
              {footer}
            </div>
          )}
        </D>
      </Modal>
    </ModalOverlay>
  );
}
